import { useMethods } from '@shared/hooks/methods'
import { useQueryClient } from '@tanstack/react-query'
import { useDialogs } from '@shared/hooks/dialogs'
import { usePermission } from '@shared/hooks/permissions'

import axios from '@shared/utils/axios'
import jsPDF from 'jspdf'
import { PDFDocument } from 'pdf-lib'

const MAX_DIMENSION = 2000
const BATCH_SIZE = 5

const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(url))
    img.src = url
})

const resizeImage = (img) => {
    let { width, height } = img
    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
    }
    const canvas = Object.assign(document.createElement('canvas'), { width, height })
    canvas.getContext('2d').drawImage(img, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95)
    canvas.width = canvas.height = 0
    return { dataUrl, width, height }
}

const generateImagePdfBuffer = async (imageList, onProgress) => {
    if (!imageList.length) return (await PDFDocument.create()).save()

    const pdf = new jsPDF({ unit: 'px', compress: true })
    let isFirstPage = true
    let completed = 0

    for (let i = 0; i < imageList.length; i += BATCH_SIZE) {
        const batch = await Promise.all(
            imageList.slice(i, i + BATCH_SIZE).map(async (url) => {
                try {
                    const img = await loadImage(`${url}?cb=${new Date().getTime()}`)
                    const result = resizeImage(img)
                    img.src = ''
                    return result
                } catch {
                    return null
                } finally {
                    onProgress(++completed)
                }
            })
        )

        for (const res of batch.filter(Boolean)) {
            const { dataUrl, width, height } = res
            if (isFirstPage) {
                pdf.internal.pageSize.width = width
                pdf.internal.pageSize.height = height
                isFirstPage = false
            } else {
                pdf.addPage([width, height], width > height ? 'l' : 'p')
            }
            pdf.addImage(dataUrl, 'JPEG', 0, 0, width, height)
        }
        await new Promise(r => setTimeout(r, 0))
    }

    if (isFirstPage) throw new Error('No valid images')
    return pdf.output('arraybuffer')
}

const mergeAttachments = async (baseBuffer, attachmentsList, startProgress, onProgress) => {
    if (!attachmentsList.length) return new Blob([baseBuffer], { type: 'application/pdf' })

    const mainDoc = await PDFDocument.load(baseBuffer)
    let completed = startProgress

    for (const url of attachmentsList) {
        try {
            const attDoc = await PDFDocument.load(await (await fetch(`${url}?cb=${new Date().getTime()}`)).arrayBuffer())
            const copiedPages = await mainDoc.copyPages(attDoc, attDoc.getPageIndices())
            copiedPages.forEach(page => mainDoc.addPage(page))
        } catch {
        } finally {
            onProgress(++completed)
        }
    }

    return new Blob([await mainDoc.save()], { type: 'application/pdf' })
}

export const useEstimation = (est) => {
    const { hasPermission } = usePermission()
    const {
        showOnly,
        closeAll
    } = useDialogs()
    const {
        ask,
        showMessage
    } = useMethods()
    const queryClient = useQueryClient()

    const handlePreview = () => {
        showOnly('previewEstimation', est)
    }

    const handleEdit = async () => {
        if (hasPermission('change_estimation')) {
            showMessage({
                text: 'جارٍ التحميل...',
                loading: true
            })
            try {
                const request = await axios.get(`estimations/${est.id}/`)
                showMessage(null)
                showOnly('estimation', request.data)
            } catch {
                showMessage({ text: 'حدث خطأ اثناء جلب المقايسة' })
            }
        } else {
            showOnly('previewEstimation', est)
        }
    }

    const handleDelete = () => {
        ask(
            `هل تريد حذف المقايسة '${est.number}'`,
            async () => {
                showMessage({
                    text: 'جارٍ الحذف...',
                    loading: true
                })
                try {
                    await axios.delete(`estimations/${est.id}/`)
                    queryClient.invalidateQueries({ queryKey: ['estimations'] })
                    queryClient.invalidateQueries({ queryKey: ['quick-look'] })
                    queryClient.invalidateQueries({ queryKey: ['offices'] })
                    showMessage({ text: 'تم حذف' })
                    closeAll()
                } catch {
                    showMessage({ text: 'حدث خطأ حذف المقايسة.' })
                }
            }
        )
    }

    const handleExport = async (objectsOnly=false) => {
        const images = est.images
            ?.filter(img => objectsOnly ? img.dep == 'documents' : true)
            ?.map(img => img.image) || []
        const attachments = objectsOnly
            ? []
            : est.attachments?.map(atch => atch.file) || []
        const totalSteps = images.length + attachments.length
        if (!totalSteps) return showMessage({ text: 'لا توجد صور أو مرفقات لهذه المقايسة' })

        showMessage({ text: `جارٍ التصدير (0/${totalSteps})`, loading: true })
        try {
            const imageBuffer = await generateImagePdfBuffer(images, (done) => 
                showMessage({ text: `جارٍ معالجة الصور (${done}/${totalSteps})`, loading: true })
            )

            const pdfBlob = await mergeAttachments(imageBuffer, attachments, images.length, (done) => 
                showMessage({ text: `جارٍ دمج المرفقات (${done}/${totalSteps})`, loading: true })
            )

            const url = URL.createObjectURL(pdfBlob)
            const a = Object.assign(document.createElement('a'), {
                href: url,
                download: `${est.number || 'مقايسة'}`.replace(/[<>:"/\\|?*\x00-\x1F\s]+/g, '_').trim() + '.pdf'
            })
            a.click()
            URL.revokeObjectURL(url)
            showMessage(null)
        } catch (e) {
            showMessage({ text: 'حدث خطأ أثناء التصدير' })
        }
    }

    const handlePin = async () => {
        showMessage({
            text: 'جارٍ التثبيت...',
            loading: true
        })
        try {
            await axios.post(`estimations/${est.id}/pin/`)
            showMessage({ text: 'تم التثبيت' })
            queryClient.invalidateQueries({ queryKey: ['estimations'] })
        } catch {
            showMessage({ text: 'حدث خطأ اثناء التثبيت.' })
        }
    }

    return {
        handlePreview,
        handleEdit,
        handleDelete,
        handleExport,
        handlePin
    }
}